import { LogoPlate } from '@/components/logo-plate';
import { copy } from '@/content/copy';
import {
  groupMethods,
  logoMonogram,
  logoPath,
  type Market,
  type PaymentMethod,
} from '@/content/payments';
import { cn } from '@/lib/cn';

const { groups: groupLabels } = copy.coverage;

/** One supported method: its logo plate beside its name. */
function MethodCard({ method }: { method: PaymentMethod }) {
  return (
    <li className="surface flex min-w-0 items-center gap-3 rounded-xl p-3">
      <LogoPlate
        src={logoPath(method.logo)}
        monogram={logoMonogram(method.logo)}
        className="h-11 w-16 rounded-lg p-1.5 text-[0.62rem]"
      />
      <span className="min-w-0 text-sm font-semibold leading-snug">{method.name}</span>
    </li>
  );
}

interface PaymentMethodsProps {
  market: Market;
  className?: string;
}

/**
 * A market's methods in full-width rows by kind, in `METHOD_KINDS` order: e-wallets, then
 * bank transfer, then USDT networks. Every row runs the same four-up grid, so a card keeps
 * one width whichever row it sits in and short rows simply end early.
 */
export function PaymentMethods({ market, className }: PaymentMethodsProps) {
  const groups = groupMethods(market);

  return (
    <div className={cn('grid gap-y-10', className)}>
      {groups.map((group) => (
        <div key={group.kind} className="grid content-start gap-4">
          <h3 className="hud m-0 text-[0.62rem] text-muted-foreground">
            {groupLabels[group.kind]}
          </h3>
          <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-4">
            {group.methods.map((method) => (
              <MethodCard key={method.name} method={method} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
